/**
 * GS-API v1 — group notification shares (GET/PUT /groups/v1/shares).
 *
 * Replaces per-grantee POST/DELETE and legacy GET /admin/notification-groups/shares
 * for single-group load/save. Hub directory still uses groups-hub/summary + legacy list.
 */

import { _sleep } from "./api.js";
import { buildContractPath } from "./api/contract-client.js";
import { buildGetGroupsV1SharesPath, buildPutGroupsV1SharesPath, buildPostGroupsV1SharesBulkRevokePath } from "./group-console-api.js";

/** @type {((path: string, opts?: object) => Promise<unknown>) | null} */
let _api = null;

/** Error code when SQLite read lock / overload persists after v1 + legacy attempts. */
export const GROUP_SHARE_LOAD_BUSY = "GROUP_SHARE_LOAD_BUSY";

const GROUP_SHARE_GET_MAX_ATTEMPTS = 6;

/**
 * Thrown when grantee preload fails after v1 retries and optional legacy fallback.
 */
export class GroupShareGranteeLoadError extends Error {
  /**
   * @param {string} message
   * @param {{ code?: string, v1Err?: unknown, legacyErr?: unknown }} [meta]
   */
  constructor(message, meta = {}) {
    super(message);
    this.name = "GroupShareGranteeLoadError";
    this.code = meta.code || "GROUP_SHARE_LOAD_FAILED";
    this.v1Err = meta.v1Err;
    this.legacyErr = meta.legacyErr;
  }
}

/**
 * @param {(path: string, opts?: object) => Promise<unknown>} apiFn
 */
export function bindGroupSharesApi(apiFn) {
  _api = apiFn;
}

function _requireApi() {
  if (!_api) throw new Error("group-shares-v1: call bindGroupSharesApi(api) first");
  return _api;
}

/**
 * @param {string} msg
 */
function _isRetryableGroupShareHttpError(msg) {
  const m = String(msg || "");
  return m.startsWith("503 ") || m.startsWith("502 ") || m.startsWith("504 ");
}

/**
 * @param {string} msg
 */
function _shouldFallbackFromV1(msg) {
  const m = String(msg || "");
  return m.startsWith("503 ") || m.startsWith("404 ") || m.startsWith("502 ") || m.startsWith("504 ");
}

/**
 * @param {Array<Record<string, unknown>>} items
 */
function _mapLegacyGranteeRows(items) {
  return items
    .map((r) => ({
      grantee_username: String(r.grantee_username || "").trim(),
      can_view: !!r.can_view,
      can_operate: !!r.can_operate,
    }))
    .filter((g) => g.grantee_username);
}

/**
 * @param {string} groupDisplay
 * @param {string | undefined} ownerScope
 */
function _buildLegacyGroupSharesPath(groupDisplay, ownerScope) {
  const query = { group_display: String(groupDisplay).trim(), limit: 500 };
  const scope = ownerScope != null ? String(ownerScope).trim() : "";
  if (scope) query.owner_scope = scope;
  return buildContractPath("get__admin_notification-groups_shares", { query });
}

/**
 * @param {ReturnType<typeof _requireApi>} api
 * @param {string} gd
 * @param {string | undefined} ownerAdmin
 * @param {number} timeoutMs
 */
async function _fetchV1GranteesWithRetry(api, gd, ownerAdmin, timeoutMs) {
  let lastErr;
  for (let attempt = 0; attempt < GROUP_SHARE_GET_MAX_ATTEMPTS; attempt++) {
    try {
      const res = await api(buildGetGroupsV1SharesPath(gd, ownerAdmin), {
        timeoutMs,
        retries: 0,
      });
      return Array.isArray(res && res.grantees) ? res.grantees : [];
    } catch (e) {
      lastErr = e;
      const msg = String((e && e.message) || e || "");
      if (attempt < GROUP_SHARE_GET_MAX_ATTEMPTS - 1 && _isRetryableGroupShareHttpError(msg)) {
        await _sleep(300 * (2 ** attempt));
        continue;
      }
      throw e;
    }
  }
  throw lastErr || new Error("group share grantee load failed");
}

/**
 * Active grantees for one notification group (GS-API GET, legacy fallback on 503/404).
 *
 * @param {{ group_display: string, owner_admin?: string, timeoutMs?: number }} opts
 * @returns {Promise<Array<{ grantee_username: string, can_view: boolean, can_operate: boolean }>>}
 */
export async function fetchGroupShareGrantees(opts) {
  const gd = String(opts.group_display || "").trim();
  if (!gd) return [];
  const api = _requireApi();
  const timeoutMs = opts.timeoutMs != null ? opts.timeoutMs : 16000;
  const owner = opts.owner_admin != null ? String(opts.owner_admin).trim() : "";
  const ownerArg = owner || undefined;
  try {
    return await _fetchV1GranteesWithRetry(api, gd, ownerArg, timeoutMs);
  } catch (v1Err) {
    const v1Msg = String((v1Err && v1Err.message) || v1Err || "");
    if (!_shouldFallbackFromV1(v1Msg)) throw v1Err;
    try {
      const res = await api(_buildLegacyGroupSharesPath(gd, ownerArg), { timeoutMs });
      return _mapLegacyGranteeRows(Array.isArray(res && res.items) ? res.items : []);
    } catch (legacyErr) {
      const legacyMsg = String((legacyErr && legacyErr.message) || legacyErr || "");
      const busy = v1Msg.startsWith("503 ") || legacyMsg.startsWith("503 ");
      throw new GroupShareGranteeLoadError(
        busy
          ? "System busy loading shared members — retry in a few seconds."
          : (legacyMsg || v1Msg || "Could not load shared members"),
        {
          code: busy ? GROUP_SHARE_LOAD_BUSY : "GROUP_SHARE_LOAD_FAILED",
          v1Err,
          legacyErr,
        },
      );
    }
  }
}

/**
 * @param {Array<{ can_view?: boolean, can_operate?: boolean }>} grantees
 */
export function granteePermissionSummary(grantees) {
  if (!grantees.length) return { can_view: true, can_operate: false };
  const v = !!grantees[0].can_view;
  const o = !!grantees[0].can_operate;
  const same = grantees.every((g) => !!g.can_view === v && !!g.can_operate === o);
  return same ? { can_view: v, can_operate: o } : { can_view: true, can_operate: false };
}

/**
 * Atomically replace active grantees (GS-API PUT). Retries 503 (write-lock busy).
 *
 * @param {{ group_display: string, owner_admin?: string, grantees: object[], timeoutMs?: number }} opts
 */
export async function syncGroupShareGrantees(opts) {
  const body = {
    group_display: String(opts.group_display || "").trim(),
    grantees: Array.isArray(opts.grantees) ? opts.grantees : [],
  };
  const oa = String(opts.owner_admin || "").trim();
  if (oa) body.owner_admin = oa;
  const timeoutMs = opts.timeoutMs != null ? opts.timeoutMs : 45000;
  const maxAttempts = 6;
  let lastErr;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      return await _requireApi()(buildPutGroupsV1SharesPath(), { method: "PUT", body, timeoutMs });
    } catch (e) {
      lastErr = e;
      const msg = String((e && e.message) || e || "");
      if (attempt < maxAttempts - 1 && msg.startsWith("503")) {
        await _sleep(300 * (2 ** attempt));
        continue;
      }
      throw e;
    }
  }
  throw lastErr || new Error("group share sync failed");
}

/**
 * Revoke all grantees on multiple hub rows in one request (GS-BULK-API-1).
 * Retries 503 (write-lock busy). Caller should fall back to sequential PUT on 404.
 *
 * @param {{ rows: Array<{ group_display: string, owner_admin?: string }>, timeoutMs?: number }} opts
 */
export async function bulkRevokeGroupShareHub(opts) {
  const rows = (Array.isArray(opts.rows) ? opts.rows : [])
    .map((r) => {
      const group_display = String(r.group_display || "").trim();
      if (!group_display) return null;
      const owner_admin = String(r.owner_admin || "").trim();
      return owner_admin ? { group_display, owner_admin } : { group_display };
    })
    .filter(Boolean);
  if (!rows.length) {
    return { ok: true, groups_processed: 0, results: [] };
  }
  const timeoutMs = opts.timeoutMs != null ? opts.timeoutMs : 90000;
  const maxAttempts = 6;
  let lastErr;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      return await _requireApi()(buildPostGroupsV1SharesBulkRevokePath(), {
        method: "POST",
        body: { rows },
        timeoutMs,
      });
    } catch (e) {
      lastErr = e;
      const msg = String((e && e.message) || e || "");
      if (attempt < maxAttempts - 1 && msg.startsWith("503")) {
        await _sleep(300 * (2 ** attempt));
        continue;
      }
      throw e;
    }
  }
  throw lastErr || new Error("group share bulk revoke failed");
}
